import type {
  StudentConversationKind,
  StudentMessage,
  StudentMessagingContacts,
} from "../../api/student-messaging.api";

// ─── Conversations avec des élèves, dans le volet Messagerie ───
// Texte seul, sans temps réel : la liste et le fil ouvert sont relus périodiquement.

/** Relecture de la liste (volet ouvert) et du fil affiché. */
export const STUDENT_REFRESH_MS = 20_000;
/** Volet replié : la liste ne sert plus qu'au compteur de non-lus. */
export const STUDENT_REFRESH_CLOSED_MS = 60_000;
export const STUDENT_MAX_BODY = 4000;

/** Mention de toute réponse de Permio AI à un élève (art. 50 du règlement européen sur l'IA) : ligne d'auteur du message, côté élève comme côté bureau. */
export const AI_REPLY_MENTION = "Réponse générée par Permio AI (une IA)";

/** Marque d'une conversation lue par un ou des élèves : jamais d'ambiguïté avec l'équipe. */
export const STUDENT_MARK: Record<StudentConversationKind, string> = {
  OFFICE: "Élève",
  DIRECT: "Élève",
  GROUP: "Groupe avec élèves",
};

/** Nature de la conversation, à côté de la marque. */
export const STUDENT_KIND_LABEL: Record<StudentConversationKind, string> = {
  OFFICE: "Boîte commune du secrétariat",
  DIRECT: "Échange à deux",
  GROUP: "Équipe et élèves",
};

/** Rappel affiché en tête d'un fil lu par un ou des élèves. */
export const studentNotice = (kind?: StudentConversationKind) =>
  kind === "GROUP"
    ? "Conversation avec des élèves : texte seul, aucune référence interne."
    : "Conversation avec un élève : texte seul, aucune référence interne.";

export const STUDENT_ROLE_LABEL: Record<string, string> = {
  owner: "Gérant",
  manager: "Responsable",
  agent: "Secrétariat",
  read_only: "Lecture seule",
  instructor: "Moniteur",
  student: "Élève",
};

/** Message du serveur, affiché tel quel ; `fallback` si la réponse n'en porte pas. */
export function studentErrorMessage(error: unknown, fallback: string): string {
  const message = (
    error as { response?: { data?: { message?: unknown } } } | null
  )?.response?.data?.message;
  if (Array.isArray(message)) return message.join(" ");
  return typeof message === "string" && message ? message : fallback;
}

/** Refus du serveur (4xx) : inutile de relire périodiquement, la réponse ne changera pas seule. */
export function isRefusal(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  return typeof status === "number" && status >= 400 && status < 500;
}

/** Réponse de `/contacts` exploitable ; toute autre forme vaut « pas d'accès ». */
export function validContacts(data: unknown): StudentMessagingContacts | null {
  const contacts = data as StudentMessagingContacts | null;
  return contacts &&
    typeof contacts === "object" &&
    !Array.isArray(contacts) &&
    Array.isArray(contacts.staff)
    ? contacts
    : null;
}

/** Réunit des pages de messages sans doublon, du plus ancien au plus récent (la dernière liste l'emporte). */
export function mergeMessages(...lists: StudentMessage[][]): StudentMessage[] {
  const bySequence = new Map<number, StudentMessage>();
  for (const list of lists)
    for (const message of list) bySequence.set(message.sequence, message);
  return [...bySequence.values()].sort((a, b) => a.sequence - b.sequence);
}

export const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
